import { useCallback, useEffect, useRef, useState } from 'react'
import { getAdvisorSupportingText, type AdvisorFeedback } from '@ppwr/convex/players/types'
import { Pressable, StyleSheet, View } from 'react-native'
import { Portal } from '@gorhom/portal'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
  type SharedValue
} from 'react-native-reanimated'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { ACTION_ADVISOR_COPY, buildActionAdvisorHeadline } from './actionAdvisor.constants'
import { getAdvisorCoachingLevel, useActionAdvisorStore } from './useActionAdvisorStore'
import { gameSheetTopY } from './GameBottomSheet/gameSheetSharedValues'

import Tail from '@/assets/Advisor/tail.svg'

type ActionAdvisorCardProps = {
  feedback?: AdvisorFeedback
  /** Replaces the action-based headline for outcome messages such as a winning-hand recap. */
  headlineOverride?: string
  /** True while it is the player's turn. The true→false edge means the player just acted. */
  isPlayersTurn: boolean
  /** True while the hand's winners are being revealed. */
  isHandOver: boolean
  /** Whether feedback is on (color + active bubble) or muted (grayscale, silent). */
  enabled: boolean
  /** Show the bubble even while muted — used for the welcome explainer, which
   * must greet the player before they have toggled feedback on. */
  showBubbleWhileMuted?: boolean
  /** Changes whenever a screen tap should dismiss the currently visible bubble. */
  dismissRequestId: number
  /** Increments when the player follows turn-start advice — the avatar
   * answers with a thumbs-up badge instead of a bubble. */
  thumbsUpRequestId: number
  /** Game mode at the mount site — stamped on the dismissed_bubble analytics
   * event. */
  gameMode?: string
  /** Toggle feedback on/off — tapping the avatar. Called with the next desired state. */
  onToggle: (next: boolean) => void
}

const BUBBLE_DURATION_MS = 12000
const BUBBLE_TRANSITION_MS = 180
const AVATAR_FADE_MS = 220
// A bubble shown within this window of the player's turn ending belongs to the action
// just taken (advice can land a tick before the turn flips), so a turn-end clear must
// not remove it. Anything older is from a previous action and is cleared on play.
const FRESH_BUBBLE_MS = 1200
// Space kept between the bubble's bottom edge and the top edge of the post-hand
// ShowCards drawer while the bubble is pinned on top of it.
const DRAWER_PIN_GAP = 12
// Vertical drift paired with the enter/exit fade: the bubble enters from below
// (slides up into place) and leaves sliding down the same amount (a gentle fall
// away as it fades). Set to 0 for a pure fade in place.
const BUBBLE_DRIFT_Y = 16
// Let entering animations (bubble fade-in, bottom-bar entrance) settle before
// reading the bubble's window-space position.
const BUBBLE_MEASURE_DELAY_MS = 300
// How long the follow-advice thumbs-up badge holds at full size before fading.
const ADVISOR_THUMBS_UP_HOLD_MS = 1100

// Maps advisor grade to headline text color
const gradeColors = {
  aligned: 'Utility/Success',
  close: 'Brand/Orange',
  offProfile: 'Utility/Error'
} as const

// One-time snapshot of the drawer pin (see DRAWER_PIN_GAP), read from the JS
// thread when a bubble exit starts so the fading bubble stays put even if the
// drawer moves mid-fade.
const pinSnapshot = (bubbleBottomY: SharedValue<number>): number => {
  const sheetTop = gameSheetTopY.value
  const bubbleBottom = bubbleBottomY.value
  if (sheetTop === Infinity || bubbleBottom === Infinity) {
    return 0
  }
  return Math.min(0, sheetTop - DRAWER_PIN_GAP - bubbleBottom)
}

// Right-edge inset for the advisor avatar (and its "ADVISOR" label). The
// speech-bubble tail sits this far to the right of the avatar (derived, not a
// second constant to keep in sync) so it stays attached to the bubble while
// still pointing at the avatar. NOTE: the tail offset is measured from the
// bubble's right edge, so the bubble needs to be wider than roughly this
// value plus the tail width (~100px) for the tail to stay attached — the
// bubble is content-sized, and headline + supporting line keep it above that.
const ADVISOR_AVATAR_RIGHT = 86
const ADVISOR_TAIL_RIGHT_OFFSET = 14

// The avatar artwork's rendered size. The avatar sources are SVGs
// (advisor-avatar.svg / advisor-avatar-disabled.svg): the artwork is a
// full-bleed circle (the circle is inscribed in the viewBox edge to edge and
// cropped to it by an internal clipPath), so one square size serves both
// axes and the visible circle fills the whole size. Rendered 4pt smaller
// than the 46pt PNG footprint the seat layout was tuned around; the nudge
// below keeps the bottom edge in place, so the trim comes off the top.
const AVATAR_ICON_SIZE = 42
// Holds the circle's bottom edge where the 46pt artwork sat: the centered
// icon is nudged down by half the size reduction (23 - AVATAR_ICON_SIZE/2),
// so scaling the avatar down only pulls its top edge down.
const AVATAR_ICON_NUDGE_Y = 2

// Portal host (mounted in the game seats layer, before the seats) that the
// advisor avatar teleports into. Rendering the avatar below the seat boxes
// keeps seated players' status ("fold", blinds, bet amounts) readable on top
// of it, while the speech bubble stays above everything in the bottom bar.
export const ADVISOR_AVATAR_HOST = 'advisor-avatar'

export const ActionAdvisorCard = ({
  feedback,
  headlineOverride,
  isPlayersTurn,
  isHandOver,
  enabled,
  showBubbleWhileMuted = false,
  dismissRequestId,
  thumbsUpRequestId,
  gameMode,
  onToggle
}: ActionAdvisorCardProps) => {
  const [activeFeedback, setActiveFeedback] = useState<AdvisorFeedback | undefined>(feedback)
  const [activeHeadlineOverride, setActiveHeadlineOverride] = useState(headlineOverride)
  const [isBubbleVisible, setIsBubbleVisible] = useState(false)
  const [isBubbleRendered, setIsBubbleRendered] = useState(false)
  // Thumbs-up badge mount gate: the badge unmounts once its fade finishes.
  // Safari leaves visual artifacts from an emoji layer parked at fractional
  // scale/opacity (the tip of the thumb sticking around), so the element must
  // leave the DOM entirely instead of resting at opacity 0.
  const [isThumbsUpBadgeVisible, setIsThumbsUpBadgeVisible] = useState(false)
  const {
    analytics: { setEvent }
  } = useStore()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const transitionRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shownAtRef = useRef(0)
  const visibleRef = useRef(false)
  const prevFeedbackRef = useRef<AdvisorFeedback | undefined>(feedback)
  const prevPlayersTurnRef = useRef(isPlayersTurn)
  const prevDismissRequestIdRef = useRef(dismissRequestId)
  const prevThumbsUpRequestIdRef = useRef(thumbsUpRequestId)
  const prevEnabledRef = useRef(enabled)
  const enabledProgress = useSharedValue(enabled ? 1 : 0)
  const avatarScale = useSharedValue(1)
  // Window-space Y of the bubble's bottom edge in its natural (untranslated)
  // position. Measured from a wrapper that never receives the drawer shift, so
  // it stays valid while the bubble itself is translated.
  const bubbleBottomY = useSharedValue(Number.POSITIVE_INFINITY)
  const bubbleAnchorRef = useRef<View>(null)
  // Hand-rolled enter/exit (fade + BUBBLE_DRIFT_Y drift) so the animation can
  // compose with the drawer pin on the same view — Reanimated's
  // entering/exiting takes over the transform, which previously let the
  // drawer drag the dying bubble around mid-exit. `bubbleFrozenPinY`
  // snapshots the pin when an exit starts so drawer motion during the fade
  // can't move the bubble.
  const bubbleOpacity = useSharedValue(0)
  const bubbleOffsetY = useSharedValue(BUBBLE_DRIFT_Y)
  const bubbleFrozenPinY = useSharedValue(0)
  const bubbleIsExiting = useSharedValue(0)
  // Thumbs-up badge (shown when the player follows turn-start advice):
  // pops in at the avatar's top-left, holds, then fades. 0 = hidden.
  const thumbsUpProgress = useSharedValue(0)

  const avatarVisualStyle = useAnimatedStyle(() => ({
    opacity: 0.55 + enabledProgress.value * 0.45,
    transform: [{ scale: avatarScale.value }]
  }))
  const enabledIconStyle = useAnimatedStyle(() => ({ opacity: enabledProgress.value }))
  const disabledIconStyle = useAnimatedStyle(() => ({ opacity: 1 - enabledProgress.value }))
  const labelStyle = useAnimatedStyle(() => ({ opacity: 0.5 + enabledProgress.value * 0.5 }))

  // The bubble's full animated style: the drawer pin (live while showing,
  // frozen while exiting) composed with the enter/exit fade & drift. The pin
  // keeps the bubble just above the post-hand ShowCards drawer's top edge;
  // clamped at 0 so a closed (or short) drawer leaves the bubble in its
  // natural spot above the avatar.
  const bubbleAnimStyle = useAnimatedStyle(() => {
    const sheetTop = gameSheetTopY.value
    const bubbleBottom = bubbleBottomY.value
    let translateY = 0
    if (sheetTop !== Infinity && bubbleBottom !== Infinity) {
      translateY = Math.min(0, sheetTop - DRAWER_PIN_GAP - bubbleBottom)
    }
    if (bubbleIsExiting.value === 1) {
      translateY = bubbleFrozenPinY.value
    }
    return {
      opacity: bubbleOpacity.value,
      transform: [{ translateY: translateY + bubbleOffsetY.value }]
    }
  })

  // Measure the bubble's natural bottom edge whenever a bubble is shown, so
  // the drawer pin above knows how far to lift it.
  useEffect(() => {
    if (!isBubbleVisible) return
    const timer = setTimeout(() => {
      bubbleAnchorRef.current?.measureInWindow((_x, y, _width, height) => {
        const bottom = (y ?? 0) + (height ?? 0)
        if (bottom > 0) {
          bubbleBottomY.value = bottom
        }
      })
    }, BUBBLE_MEASURE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [isBubbleVisible, bubbleBottomY])

  // Single owner of the bubble's mount + enter/exit animation. Every hide
  // path (12s timeout, dismiss request, hand over, toggle off, feedback
  // cleared) funnels through `shouldRenderBubble` going false, which freezes
  // the drawer pin and fades the bubble out in place; the view unmounts once
  // the fade completes. Reanimated's entering/exiting is deliberately not
  // used here — it owns the transform and would fight the pin.
  const shouldRenderBubble = (enabled || showBubbleWhileMuted) && isBubbleVisible && !!activeFeedback
  useEffect(() => {
    if (shouldRenderBubble) {
      bubbleIsExiting.value = 0
      if (!isBubbleRendered) {
        // Fresh show: start from below the resting spot so the enter slides
        // up into place (after a completed exit the offset rests at +drift).
        bubbleOffsetY.value = BUBBLE_DRIFT_Y
      }
      setIsBubbleRendered(true)
      bubbleOpacity.value = withTiming(1, { duration: BUBBLE_TRANSITION_MS })
      bubbleOffsetY.value = withTiming(0, { duration: BUBBLE_TRANSITION_MS })
      return
    }
    if (!isBubbleRendered) return
    // Snapshot the pin before flagging the exit (order matters: the style
    // worklet reads the frozen value the moment `bubbleIsExiting` flips) so
    // a drawer opening/closing during the fade can't drag the bubble.
    bubbleFrozenPinY.value = pinSnapshot(bubbleBottomY)
    bubbleIsExiting.value = 1
    bubbleOpacity.value = withTiming(0, { duration: BUBBLE_TRANSITION_MS })
    bubbleOffsetY.value = withTiming(BUBBLE_DRIFT_Y, { duration: BUBBLE_TRANSITION_MS })
    const timer = setTimeout(() => setIsBubbleRendered(false), BUBBLE_TRANSITION_MS + 40)
    return () => clearTimeout(timer)
  }, [
    shouldRenderBubble,
    isBubbleRendered,
    bubbleOpacity,
    bubbleOffsetY,
    bubbleFrozenPinY,
    bubbleIsExiting,
    bubbleBottomY
  ])

  // One effect owns the whole lifecycle so "new advice arrived" and "player acted"
  // can be resolved against each other without cross-effect ordering races.
  useEffect(() => {
    const prevFeedback = prevFeedbackRef.current
    const prevPlayersTurn = prevPlayersTurnRef.current
    prevFeedbackRef.current = feedback
    prevPlayersTurnRef.current = isPlayersTurn
    const feedbackChanged = feedback !== prevFeedback
    const justActed = prevPlayersTurn && !isPlayersTurn

    const clearTimers = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      if (transitionRef.current) {
        clearTimeout(transitionRef.current)
        transitionRef.current = null
      }
    }

    const hide = (clearActiveFeedback = false) => {
      clearTimers()
      setIsBubbleVisible(false)
      visibleRef.current = false
      if (clearActiveFeedback) {
        transitionRef.current = setTimeout(() => {
          setActiveFeedback(undefined)
          setActiveHeadlineOverride(undefined)
        }, BUBBLE_TRANSITION_MS)
      }
    }

    const show = (next: AdvisorFeedback) => {
      clearTimers()
      setActiveFeedback(next)
      setActiveHeadlineOverride(headlineOverride)
      setIsBubbleVisible(true)
      visibleRef.current = true
      shownAtRef.current = Date.now()
      timeoutRef.current = setTimeout(() => {
        setIsBubbleVisible(false)
        visibleRef.current = false
      }, BUBBLE_DURATION_MS)
    }

    // 1. New advice replaces the visible message immediately. Delaying it behind an
    // exit animation disconnects the feedback from the action that produced it.
    if (feedbackChanged && feedback) {
      show(feedback)
      return
    }

    // 2. Advice cleared externally (e.g. scope/hand change) → hide.
    if (feedbackChanged && !feedback) {
      hide(true)
      return
    }

    // 3. Player acted but no new advice came (non-meaningful spot) → clear the stale bubble.
    // Skip a bubble that was just shown for this very action (advice can land a tick
    // before the turn flips), which would otherwise be cleared the instant it appeared.
    if (justActed && visibleRef.current && Date.now() - shownAtRef.current >= FRESH_BUBBLE_MS) {
      hide(true)
      return
    }

    // 4. Hand is over → dismiss promptly so the bubble never lingers into the
    //    next hand or overlaps the winners reveal.
    if (isHandOver && visibleRef.current) {
      hide(true)
    }
  }, [feedback, headlineOverride, isPlayersTurn, isHandOver])

  useEffect(() => {
    if (dismissRequestId === prevDismissRequestIdRef.current) return
    const wasVisible = visibleRef.current
    prevDismissRequestIdRef.current = dismissRequestId

    // Analytics: a table tap dismissed a VISIBLE bubble (onTouchStart fires on
    // every table touch — only the ones that actually closed a bubble count).
    if (wasVisible) {
      setEvent(events.advisor.dismissed_bubble, {
        coachingLevel: getAdvisorCoachingLevel(useActionAdvisorStore.getState()),
        gameMode,
        messageKind: activeFeedback?.conversation?.kind ?? (activeHeadlineOverride ? 'outcome' : 'unknown')
      })
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (transitionRef.current) clearTimeout(transitionRef.current)
    timeoutRef.current = null
    transitionRef.current = null
    setIsBubbleVisible(false)
    visibleRef.current = false
  }, [dismissRequestId, setEvent, activeFeedback, activeHeadlineOverride, gameMode])

  useEffect(() => {
    if (thumbsUpRequestId === prevThumbsUpRequestIdRef.current) return
    prevThumbsUpRequestIdRef.current = thumbsUpRequestId

    // Pop in above resting size, settle, hold, then fade out in place. The
    // badge unmounts when the fade finishes (see isThumbsUpBadgeVisible) so
    // no emoji layer rests at opacity 0 — Safari leaves ghost pixels from
    // that (the tip of the thumb sticking around after the animation).
    setIsThumbsUpBadgeVisible(true)
    thumbsUpProgress.value = 0
    thumbsUpProgress.value = withSequence(
      withTiming(1.25, { duration: 140 }),
      withTiming(1, { duration: 90 }),
      withDelay(
        ADVISOR_THUMBS_UP_HOLD_MS,
        withTiming(0, { duration: 320 }, (finished) => {
          if (finished) {
            runOnJS(setIsThumbsUpBadgeVisible)(false)
          }
        })
      )
    )
  }, [thumbsUpRequestId, thumbsUpProgress])

  const thumbsUpStyle = useAnimatedStyle(() => ({
    opacity: Math.min(1, thumbsUpProgress.value * 1.6),
    transform: [{ scale: Math.max(0.001, thumbsUpProgress.value) }]
  }))

  useEffect(() => {
    const didToggle = prevEnabledRef.current !== enabled
    prevEnabledRef.current = enabled
    enabledProgress.value = withTiming(enabled ? 1 : 0, { duration: AVATAR_FADE_MS })

    if (didToggle) {
      avatarScale.value = withSequence(withTiming(1.03, { duration: 40 }), withTiming(1, { duration: 70 }))
    }
  }, [avatarScale, enabled, enabledProgress])

  // Clear any pending timers on unmount only.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (transitionRef.current) clearTimeout(transitionRef.current)
    }
  }, [])

  const handleToggle = useCallback(() => {
    onToggle(!enabled)
  }, [enabled, onToggle])

  const supportingText = activeFeedback ? getAdvisorSupportingText(activeFeedback) : ''
  const headlineText = activeFeedback ? (activeHeadlineOverride ?? buildActionAdvisorHeadline(activeFeedback)) : ''
  const headlineTextColor = activeFeedback ? gradeColors[activeFeedback.grade] : 'Brand/Dark Grey'

  return (
    <View pointerEvents="box-none" style={styles.anchor}>
      {/* Stable measuring wrapper: holds the bubble's natural placement and
          never receives the drawer shift, so its window position stays valid. */}
      <View ref={bubbleAnchorRef} collapsable={false} pointerEvents="box-none" style={styles.bubbleAnchor}>
        {isBubbleRendered ? (
          <Animated.View style={[styles.bubble, bubbleAnimStyle]}>
            <View style={{ position: 'absolute', bottom: -9, right: ADVISOR_AVATAR_RIGHT + ADVISOR_TAIL_RIGHT_OFFSET }}>
              <Tail height={17} />
            </View>

            <DS.Text style={{ textAlign: 'right' }} variant="Body Small/Medium" color={headlineTextColor}>
              {headlineText}
            </DS.Text>
            {/* Brand/Green is the palette's darkest grey (same green-grey hue as
                Brand/Dark Grey, darker); Dark Grey at caption size was too
                faint to read in the bubble. */}
            <DS.Text style={{ textAlign: 'right' }} variant="Body Small/Regular" color="Brand/Green">
              {supportingText}
            </DS.Text>
          </Animated.View>
        ) : null}
      </View>

      {/* The avatar teleports below the seat layer (see PortalHost in
          GameContainer) so seat status badges paint above it. */}
      <Portal hostName={ADVISOR_AVATAR_HOST}>
        <View style={styles.avatarLayer}>
          <View style={styles.controls}>
            <Pressable
              onPress={handleToggle}
              hitSlop={12}
              accessibilityRole="button"
              accessibilityState={{ checked: enabled }}
              accessibilityLabel={`${ACTION_ADVISOR_COPY.drawerLabel} ${enabled ? 'on' : 'off'}`}
              style={styles.avatar}
            >
              <Animated.View pointerEvents="none" style={[styles.avatarVisual, avatarVisualStyle]}>
                {/* Circular crop for the icon layers. The avatar SVGs crop
                    their artwork with an internal clipPath, but the svg
                    transformer (svgo cleanupIds) minifies the clip id per
                    file, and on web every SVG shares one DOM, so the
                    minified id can collide with other icons' clip ids and
                    the reference resolves to a foreign full-square clip
                    (the avatar renders as a square; native scopes ids per
                    SVG, so it only breaks on web). The SVG's clip circle is
                    exactly the viewBox-inscribed circle, so an icon-sized
                    wrapper with a circular radius crops identically on
                    every platform with no id references. */}
                <View pointerEvents="none" style={styles.avatarIconCrop}>
                  <Animated.View style={[styles.iconLayer, enabledIconStyle]}>
                    <DS.Icon name="Advisor-avatar" size={AVATAR_ICON_SIZE} />
                  </Animated.View>
                  <Animated.View style={[styles.iconLayer, disabledIconStyle]}>
                    <DS.Icon name="Advisor-avatar-disabled" size={AVATAR_ICON_SIZE} />
                  </Animated.View>
                </View>
              </Animated.View>
              {/* Follow-advice acknowledgment: pops in at the avatar's top-left
                  instead of a confirmation bubble. Sits outside the rounded
                  avatar (overflow visible) like a notification badge.
                  Conditionally mounted so nothing rests at opacity 0 (Safari
                  leaves emoji ghost pixels there). */}
              {isThumbsUpBadgeVisible ? (
                <Animated.View pointerEvents="none" style={[styles.thumbsUpBadge, thumbsUpStyle]}>
                  {/* Line-height override: the variant's 19.2 line box is
                      shorter than the emoji glyph at 16pt, which clips the
                      thumb's tip at the top (iOS). Give the line box
                      glyph-height headroom; the badge's top offset absorbs
                      the extra leading so the resting position is unchanged. */}
                  <DS.Text variant="Body Medium/Medium" allowFontScaling={false} style={styles.thumbsUpText}>
                    👍
                  </DS.Text>
                </Animated.View>
              ) : null}
              <Animated.View pointerEvents="none" style={labelStyle}>
                <DS.TextScaled variant="Caption/Bold" color="Brand/Cream" numberOfLines={1} style={styles.avatarLabel}>
                  {ACTION_ADVISOR_COPY.avatarLabel}
                </DS.TextScaled>
              </Animated.View>
            </Pressable>
          </View>
        </View>
      </Portal>
    </View>
  )
}

const styles = StyleSheet.create({
  anchor: {
    position: 'absolute',
    right: DS.spacing[16],
    // bottom-right corner stays in the same place: growing the avatar by ~10%
    // (36→40) pushes the anchor's bottom edge down by 4px, so move the top up 4px
    // to compensate and keep the avatar's bottom-right at the same location.
    top: -72,
    // The avatar itself now renders below the seat layer via a Portal (see
    // styles.avatarLayer); this height stands in for its row (40px avatar +
    // label) so the bubble keeps the same gap above the avatar's spot.
    height: 60,
    alignItems: 'flex-end',
    zIndex: 6
  },
  avatarLayer: {
    // The spot the avatar occupied inside the anchor (see ADVISOR_AVATAR_RIGHT):
    // the seats layer this renders in ends exactly where the bottom bar
    // begins, so bottom: 12 mirrors the anchor's top: -72 + height: 60
    // footprint.
    position: 'absolute',
    right: ADVISOR_AVATAR_RIGHT,
    bottom: 12
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DS.spacing[8]
  },
  avatar: {
    alignItems: 'center',
    // Gap between the avatar visual and the "ADVISOR" label (the Pressable's
    // only two children), so this is the label's offset from the artwork.
    gap: 6,
    overflow: 'visible'
  },
  avatarVisual: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Notification-badge placement: the avatar visual is 40x40 at the top of
  // the Pressable (the label sits below it), so anchor to the visual's
  // top-left corner, slightly outside it.
  thumbsUpBadge: {
    position: 'absolute',
    top: -9,
    left: -12,
    zIndex: 1
  },
  thumbsUpText: {
    lineHeight: 23
  },
  avatarLabel: {
    alignSelf: 'center'
  },
  iconLayer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Icon-bounds circular crop (see the comment at the usage site): same
  // circle the SVG's internal clipPath uses, so native rendering is
  // unchanged and web gets the same crop without id-reference collisions.
  // Absolute with no insets so the icon centers on the 40x40 visual (a
  // parent's center alignment centers inset-less absolute children), with
  // the nudge below holding the bottom edge in place at the smaller size.
  avatarIconCrop: {
    position: 'absolute',
    width: AVATAR_ICON_SIZE,
    height: AVATAR_ICON_SIZE,
    borderRadius: AVATAR_ICON_SIZE / 2,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ translateY: AVATAR_ICON_NUDGE_Y }]
  },
  bubbleAnchor: {
    // Same placement the bubble used to own, so its natural position above
    // the avatar is unchanged when no drawer is presented.
    position: 'absolute',
    right: 14,
    bottom: 64,
    width: 300
  },
  bubble: {
    paddingHorizontal: DS.spacing[12],
    paddingVertical: DS.spacing[8],
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[16],
    borderWidth: 1,
    borderColor: DS.palette['Global/White'],
    gap: DS.spacing[6]
  }
})
